import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, CheckCircle, ArrowRight, BarChart, Send } from 'lucide-react';

const ComplianceMonitoring = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', company: '', phone: '', email: '',
    facilityType: '', permitType: '', monitoringNeeds: '', currentMonitoring: '',
    reportingFrequency: '', complianceIssues: '', specificRequirements: '', additionalInfo: ''
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/compliance-monitoring`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your compliance monitoring inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityType: '', permitType: '', monitoringNeeds: '', currentMonitoring: '',
          reportingFrequency: '', complianceIssues: '', specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  const services = [
    { title: 'Emission Monitoring', desc: 'CEMS, stack testing, and periodic monitoring programs.', items: ['Continuous monitoring systems', 'Stack testing coordination', 'Portable analyzer programs', 'Quality assurance procedures'] },
    { title: 'Recordkeeping Systems', desc: 'Comprehensive data management and documentation.', items: ['Database design', 'Electronic recordkeeping', 'Document management', 'Data backup systems'] },
    { title: 'Compliance Reporting', desc: 'Timely and accurate regulatory report preparation.', items: ['Annual emissions inventories', 'Semi-annual reports', 'Deviation reports', 'Compliance certifications'] },
    { title: 'Performance Testing', desc: 'EPA Method testing and compliance demonstrations.', items: ['Test protocol development', 'Contractor coordination', 'Witness testing', 'Report review'] }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative bg-gradient-to-r from-purple-900 to-purple-700 text-white py-20" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw2fHxkYXNoYm9hcmQlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzMwODQxNjAwfDA&ixlib=rb-4.1.0&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <ClipboardCheck className="w-16 h-16 mr-4" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Compliance Monitoring</h1>
              <p className="text-xl md:text-2xl max-w-3xl">Ongoing monitoring, recordkeeping, and reporting strategies</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Environmental Compliance & Reporting</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Staying compliant in the Interior requires more than just filing paperwork. We provide comprehensive compliance management tailored to remote operations.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Emissions Inventories</h3>
                <p className="text-gray-700">Accurate development of Triennial Emission Inventories.</p>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">GHG Reporting</h3>
                <p className="text-gray-700">Mandatory reporting support for 40 CFR Part 98.</p>
              </div>
              
              <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Reporting</h3>
                <p className="text-gray-700">Preparation of Facility Operating Reports (FOR) and Excess Emissions Reports (EER).</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Compliance Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-t-4 border-purple-700">
                <div className="flex items-center mb-3">
                  <BarChart className="w-6 h-6 text-purple-700 mr-3" />
                  <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{service.desc}</p>
                <ul className="space-y-2">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-purple-700 mr-2 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-purple-700 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request Compliance Monitoring Support</h2>
                  <p className="text-purple-100 mt-2">Get help with your compliance monitoring program</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl">
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required placeholder="First Name *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Last Name *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input type="text" name="company" value={formData.company} onChange={handleChange} required placeholder="Company *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Phone *" className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email *" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Facility Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <select name="facilityType" value={formData.facilityType} onChange={handleChange} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option value="">Facility Type</option>
                    <option value="power">Power Generation</option>
                    <option value="oil-gas">Oil & Gas</option>
                    <option value="industrial">Industrial</option>
                    <option value="other">Other</option>
                  </select>
                  <select name="permitType" value={formData.permitType} onChange={handleChange} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                    <option value="">Permit Type</option>
                    <option value="title-v">Title V</option>
                    <option value="minor">Minor Source</option>
                    <option value="psd">PSD</option>
                    <option value="general">General Permit</option>
                  </select>
                </div>
                <textarea name="monitoringNeeds" value={formData.monitoringNeeds} onChange={handleChange} rows="2" placeholder="Describe monitoring needs" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-4"></textarea>
                <textarea name="currentMonitoring" value={formData.currentMonitoring} onChange={handleChange} rows="2" placeholder="Current monitoring setup (if any)" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-4"></textarea>
                <input type="text" name="reportingFrequency" value={formData.reportingFrequency} onChange={handleChange} placeholder="Required reporting frequency" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-4" />
                <textarea name="complianceIssues" value={formData.complianceIssues} onChange={handleChange} rows="2" placeholder="Any compliance challenges or concerns" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-4"></textarea>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., CEMS setup, stack testing needs, reporting requirements, specific compliance concerns, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  ></textarea>
                </div>
                <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="3" placeholder="Additional information" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"></textarea>
              </div>

              <div className="flex justify-center">
                <button type="submit" className="inline-flex items-center px-8 py-4 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors shadow-lg">
                  Submit Monitoring Inquiry <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help with Compliance Monitoring?</h2>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">Contact us to discuss your monitoring and reporting requirements.</p>
          <Link to="/contact" className="inline-flex items-center px-8 py-4 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors shadow-lg">
            Contact Us <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ComplianceMonitoring;
